import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format } from 'date-fns';

import { useToast } from '../../../hooks/toast';
import { useAgendamento } from '../../../hooks/agendamento';

import api from '../../../services/api';

import SelectSync, { IOption } from '../../../components/SelectSync';
import Input from '../../../components/Input';
import Loading from '../../../components/Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
  TIPO_PERFIL,
} from '../../../utils/funcoes';
import getValidationErrors from '../../../utils/getValidationErrors';
import getResponseErrors from '../../../utils/getResponseErrors';
import { IAgendamento } from '../../../components/Agendamento';
import { useAuth } from '../../../hooks/auth';
import { PermissaoAdmin } from '../../../components/Agendamento/styles';

import { IPerfil, usePerfil } from '../../../hooks/perfil';

interface IFormData {
  AgeData: Date;
  AgeHora: string;
  AgeConfCodigo: string;
  login?: string;
  senha?: string;
}

export interface IReturnAgendamentoTratamento {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeConfCodigo: number;
  updated_at: Date;
  valor: number;
}

interface IDadosAgendamento {
  ConCodigo: number;
  AgeClinica: number;
}

interface IAgendamentoAdicionado extends IAgendamento {
  AgeCodigo: number;
}

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
  ProDuracao: number;
}

type ComponentProps = {
  checkupId: number;
  pacCodigo: number;

  handleFechar(dados: IReturnAgendamentoTratamento): void;
};

const CheckupAgendamentoConsulta: React.FC<ComponentProps> = ({
  checkupId,
  pacCodigo,

  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { clinica } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const { perfis, buscaPerfil } = usePerfil();
  const { buscaProcedimentos } = useAgendamento();
  const [optionPerfil, setOptionsPerfil] = useState<IOption[]>([]);
  const [procedimento, setProcedimento] = useState<IProcedimento | undefined>();

  const [perfilSelecionado, setPerfilSelecionado] = useState<string>();

  const [optionsAgendas, setOptionsAgendas] = useState<IOption[] | undefined>(
    undefined,
  );
  const [exibePermissaoAdmin, setExibePermissaoAdmin] = useState(false);
  const [mensagemPermissaoAdmin, setMensagemPermissaoAdmin] = useState('');

  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!perfis.perfis || perfis.perfis.length === 0) loadPerfis();
  }, [buscaPerfil, perfis]);

  useEffect(() => {
    try {
      const options: IOption[] = perfis.perfis
        .filter(
          (per: IPerfil) =>
            per.PerTipo === TIPO_PERFIL.CONSULTA && per.PerAtivo,
        )
        .map((op: IPerfil) => ({
          value: op.PerCodigo.toString(),
          label: op.PerNome,
          isDisabled: !op.PerAtivo,
        }));
      setOptionsPerfil(options);
    } catch (error) {
      addToast({
        title: 'Não foi possível buscar perfis',
        type: 'error',
      });
    }
  }, [addToast, perfis.perfis]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (perfilSelecionado) {
          // selecionar agendas do perfil

          const responseAgenda = await api.get<IOption[]>(
            `/agendas/porperfil/transferir?ConPerCodigo=${perfilSelecionado}`,
          );

          setOptionsAgendas(responseAgenda.data);

          const respostaPro = await buscaProcedimentos(perfilSelecionado);

          console.log(respostaPro);
          if (respostaPro) {
            const tratamento = respostaPro.procedimentos.find(proc =>
              proc.ProNome.includes('CONSULTA'),
            );
            setProcedimento(tratamento);
            console.log(tratamento);
          }
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (checkupId) loadDados();
  }, [clinica.CliCodigo, perfilSelecionado, checkupId, addToast]);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        if (!procedimento) {
          addToast({ title: 'Procedimento não encontrado.', type: 'error' });
          return;
        }

        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
          AgeConfCodigo: Yup.string().required('Agenda obrigatória'),
          ...(exibePermissaoAdmin
            ? {
                login: Yup.string().required('Login é obrigatório'),
                senha: Yup.string().required('Senha é obrigatória'),
              }
            : {}),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, procedimento.ProDuracao),
          AgePacCodigo: pacCodigo,
          AgeFormaPag: 9,
          AgeClinica: clinica.CliCodigo,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: false,
          ...(exibePermissaoAdmin
            ? { permissao: { UsuLogin: dados.login, UsuSenha: dados.senha } }
            : {}),
          duracao: procedimento.ProDuracao,
          procedimentos: [
            {
              ProCodigo: procedimento.ProCodigo,
              ProNome: procedimento.ProNome,
              ProValor: 0,
            },
          ],
        };

        const agendamentoAdicionado = await api.post<IAgendamentoAdicionado>(
          `/agendamentos/checkup/${checkupId}`,
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda

        addToast({ title: 'Agendamento salvo', type: 'success' });

        handleFechar({
          AgeCodigo: agendamentoAdicionado.data.AgeCodigo,
          AgeInicio: agendamentoAdicionado.data.AgeInicio,
          AgeInicioFormatado: format(
            parseISO(agendamentoAdicionado.data.AgeInicio.toString()),
            'dd/MM/yyyy HH:mm',
          ),
          AgeConfCodigo: agendamentoAdicionado.data.AgeConfCodigo,
          updated_at: agendamentoAdicionado.data.updated_at,
          valor: 0,
        });
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        const statusErro = error?.response?.status;
        if (statusErro === 403) {
          setExibePermissaoAdmin(true);
          setMensagemPermissaoAdmin(mensagemErro);
        }
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
          timeAlive: 10000,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      checkupId,
      pacCodigo,
      clinica.CliCodigo,
      procedimento,
      handleFechar,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);
  const handleOnChangePerfil = useCallback((op: IOption) => {
    setPerfilSelecionado(op.value);
  }, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      <>
        <h5>Gerar agendamento</h5>
        {optionPerfil && (
          <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
            {exibePermissaoAdmin && (
              <PermissaoAdmin className="shadow border">
                <div>
                  <h6>Permissão necessária</h6>
                  <p>{mensagemPermissaoAdmin}</p>
                  <Input name="login" label="Código ou Email" />
                  <Input
                    name="senha"
                    label="Senha"
                    autoComplete="off"
                    type="password"
                  />
                  <Button
                    type="submit"
                    variant="primary mt-2"
                    size="sm"
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Confirmar'}
                  </Button>

                  <Button
                    type="button"
                    variant="outline-secondary mt-2 float-right"
                    size="sm"
                    disabled={loading}
                    onClick={() => {
                      setExibePermissaoAdmin(false);
                      setMensagemPermissaoAdmin('');
                    }}
                  >
                    Voltar
                  </Button>
                </div>
              </PermissaoAdmin>
            )}
            <Row className="mb-2">
              <Col md={4} lg={5} className="mt-2">
                <SelectSync
                  name="PerCodigo"
                  label="Perfil"
                  options={optionPerfil}
                  isClearable
                  handleSelectChange={handleOnChangePerfil}
                />
              </Col>
              {optionsAgendas && (
                <>
                  <Col md={5} sm={6}>
                    <SelectSync
                      name="AgeConfCodigo"
                      label="Agenda"
                      placeholder="Selecione a agenda"
                      isClearable={false}
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsAgendas}
                      isDisabled={perfilSelecionado === undefined}
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <Input name="AgeData" label="Data" type="date" />
                  </Col>
                  <Col md={2} sm={6}>
                    <SelectSync
                      name="AgeHora"
                      label="Hora"
                      placeholder="Selecione"
                      isClearable={false}
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsHora}
                    />
                  </Col>
                  <Col md={2} sm={6}>
                    <br />
                    <Button
                      type="submit"
                      size="sm"
                      className="mt-1"
                      disabled={loading}
                    >
                      Gerar
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        )}
      </>
    </Container>
  );
};

export default CheckupAgendamentoConsulta;
